import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import { getThumbBySize } from '../../../../util/photo';
import WatermarkOverlay from '../../../../components/global/WatermarkOverlay';
import { IMAGE_BLUR_TYPES } from '../../../../constants/image-blur-types';
import { MATCHED_PHOTO_WATERMARK_MODE } from '../../../../constants/pro-settings';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    thumbnail_urls: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
        size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)).isRequired,
      }).isRequired,
    ).isRequired,
  }).isRequired,
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const CartItemPhotoPreview = ({
  photo,
  className,
  children,
  watermarkSettings,
}) => {
  const classes = classNames({
    'checkout-cart-item-photo': true,
    [className]: !!className,
  });

  const isEntitled =
    typeof photo?.is_entitled_matched_photo === 'undefined' ||
    photo?.is_entitled_matched_photo;

  const thumbnail = useMemo(() => {
    if (
      !isEntitled &&
      watermarkSettings?.watermarkMode ===
        MATCHED_PHOTO_WATERMARK_MODE.WALLPAPER &&
      photo?.foreground?.watermarked_thumbnail_urls?.length > 0
    ) {
      return photo?.foreground?.watermarked_thumbnail_urls?.[0]?.url;
    }

    if (
      !isEntitled &&
      photo?.faceblocked_thumbnail_urls?.find(
        ({ blur_type: blurType }) =>
          blurType === IMAGE_BLUR_TYPES.PHOTO_FOREGROUND_FOREGROUND,
      )
    ) {
      return photo?.faceblocked_thumbnail_urls?.filter(
        ({ blur_type: blurType }) =>
          blurType === IMAGE_BLUR_TYPES.PHOTO_FOREGROUND_FOREGROUND,
      )?.[0]?.url;
    }

    if (photo?.foreground?.thumbnail_urls?.length > 0) {
      return photo?.foreground?.thumbnail_urls[0].url;
    }

    return getThumbBySize(photo?.thumbnail_urls, IMAGE_SIZE_CODE.SMALL2X)?.url;
  }, [photo]);

  const backgroundStyles = useMemo(() => {
    if (photo?.print_background?.id) {
      return {
        backgroundImage: `url(${photo.print_background.asset_image.url})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      };
    }

    return {};
  }, [photo, isEntitled]);

  return (
    <div className={classes} style={{ ...backgroundStyles }}>
      <img src={thumbnail} className="checkout-cart-item-photo-img" alt="" />
      {!isEntitled && !photo?.foreground?.original_url && (
        <div className="watermark-container">
          <WatermarkOverlay
            text={watermarkSettings?.wallpaperCustomText}
            fontSize={watermarkSettings?.wallpaperFontSize}
            wallpaperMode={watermarkSettings?.wallpaperWatermarkMode}
            imageUrl={watermarkSettings?.wallpaperAssetImage?.url}
          />
        </div>
      )}

      {children}
    </div>
  );
};

CartItemPhotoPreview.propTypes = propTypes;

export default CartItemPhotoPreview;
